import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';

import { mutations, queries } from '@/apollo';
import { setCookie, deleteCookie, getCookie } from '@/helpers/cookies.ts';

import type { Mutation, UserLoginPayload, Query } from '@/generated/graphql';
import { UserRoleEnum } from '@/generated/graphql';
import type { IUserLogin } from '@/helpers/types';
import { apolloService } from '@/apollo/apolloRequests';
import { useStorage, StorageSerializers } from '@vueuse/core';
import { useAccountantInfoStore } from '@/store/useAccountantInfoStore.ts';
import { useBreadcrumbsStore } from '@/store/useBreadcrumbsStore.ts';
import { useMarketsStore } from '@/components/MarketSelector/useMarketsStore.ts';

// Global file download subscription instance
let globalFileDownloadSubscription: { startSubscription: () => void; stopSubscription: () => void } | null = null;

export const setGlobalFileDownloadSubscription = (subscription: { startSubscription: () => void; stopSubscription: () => void }) => {
  globalFileDownloadSubscription = subscription;
};

export const useUserStore = defineStore('userStore', () => {
  const router = useRouter();
  const isAuthenticated = ref<boolean>(false);
  const loading = ref<boolean>(false);
  const loginData = useStorage<UserLoginPayload | null>('loginData', null, undefined, { serializer: StorageSerializers.object });
  const breadStore = useBreadcrumbsStore();
  const isManagerRole = computed(() => loginData.value?.user?.role === UserRoleEnum.Manager);
  const isAccountantRole = computed(() => loginData.value?.user?.role === UserRoleEnum.Accountant);

  // Initialize authentication state from stored data and cookie
  const initializeAuthState = () => {
    const token = getCookie('sid');
    if (token && loginData.value?.user?.id) {
      isAuthenticated.value = true;
    } else {
      isAuthenticated.value = false;
      if (!token) {
        // Clear stored data if no token
        loginData.value = null;
      }
    }
  };

  // Initialize on store creation
  initializeAuthState();

  const setLoading = (state: boolean) => {
    loading.value = state;
  };

  const loginByToken = async (token: string): Promise<void> => {
    const data = (await apolloService.makeApolloRequest('query', queries.USER_ME)) as Query;

    if (data) {
      const mutationData = {
        user: {
          login: {
            user: data?.user?.me?.user,
            sid: token,
          },
        },
      };

      handleLoginResponse(mutationData as Mutation);
      await useAccountantInfoStore().getAccountantInfo();
    } else {
      throw new Error('Unhandled response!');
    }
  };

  const login = async (payload: IUserLogin): Promise<void> => {
    setLoading(true);
    try {
      const data = (await apolloService.makeApolloRequest('mutation', mutations.USER_LOGIN, {
        login: payload.login,
        password: payload.password,
      })) as Mutation;

      if (data) {
        handleLoginResponse(data);
        await useAccountantInfoStore().getAccountantInfo();
        await router.push({ name: 'Companies' });
      } else {
        throw new Error('Unhandled response!');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoginResponse = (data: Mutation) => {
    loginData.value = data?.user.login;
    isAuthenticated.value = true;
    setCookie('sid', data?.user.login.sid ?? '', {
      secure: true,
      sameSite: 'Lax',
    });

    // Start global file download subscription after login
    if (globalFileDownloadSubscription) {
      globalFileDownloadSubscription.startSubscription();
    }
  };

  const handleLogoutResponse = () => {
    // Stop global file download subscription before logout
    if (globalFileDownloadSubscription) {
      globalFileDownloadSubscription.stopSubscription();
    }

    deleteCookie('sid');
    isAuthenticated.value = false;
    loginData.value = null;
    useAccountantInfoStore().clearAccountantInfo();
    useMarketsStore().clearMarkets();
    breadStore.drop();
  };

  const logout = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = (await apolloService.makeApolloRequest(
        'mutation',
        mutations.USER_LOGOUT,
      )) as Mutation;

      if (data) {
        handleLogoutResponse();
        await router.push({ name: 'Login' });
      } else {
        throw new Error('Unhandled response!');
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loginData,
    isAuthenticated,
    loading,
    isManagerRole,
    isAccountantRole,
    login,
    logout,
    loginByToken,
    initializeAuthState,
  };
});
